import { Flex } from "~/components/Flex";
import {
  alpha,
  Button,
  ButtonProps,
  CircularProgress,
  TextFieldProps,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useRef } from "react";
import { Close } from "@mui/icons-material";
import { WorkSpaceConditionValuesNameType } from "~/pages/space/workspaces/components/ConditionBuilder/types";
import { ConditionTagValue } from "~/components/ConditionBuilder/ConditionTagValue/ConditionTagValue";
import { WorkspaceConditionValuesAutocomplete } from "./ConditionAutocomplete/WorkspaceConditionValuesAutocomplete";

export type ConditionValue = string | { key: string; value?: string | null };

interface WorkspaceConditionValuesProps {
  values: ConditionValue[];
  showRemoveConditionButton: boolean;
  onRemove: ButtonProps["onClick"];
  isKeySelected: boolean;
  onValueRemove: (value: ConditionValue) => void;
  name: WorkSpaceConditionValuesNameType;
  conditionFormDictionaryKey: string;
  options: Array<{ label: string; value: string }>;
  isReadonly: boolean;
  isLoading: boolean;
  isDictionaryKeySelected: boolean;
  onDropdownSearchFieldKeyUp: React.KeyboardEventHandler<HTMLInputElement>;
  onDropdownSearchFieldChange: TextFieldProps["onChange"];
  isValuesDropdownOpen: boolean;
  onValuesDropdownOpen: () => void;
  onValuesDropdownClose: () => void;
  onItemClick: (value: unknown) => void;
  handleKeyValueKeyClick: (value: string) => void;
  handleKeyValueValueClick: (value: string) => void;
}

export const WorkspaceConditionValues = ({
  isKeySelected,
  values,
  showRemoveConditionButton,
  onRemove,
  onValueRemove,
  name,
  conditionFormDictionaryKey,
  options,
  isReadonly,
  isLoading,
  isDictionaryKeySelected,
  onDropdownSearchFieldKeyUp,
  onDropdownSearchFieldChange,
  onItemClick,
  isValuesDropdownOpen,
  onValuesDropdownOpen,
  onValuesDropdownClose,
  handleKeyValueKeyClick,
  handleKeyValueValueClick,
}: WorkspaceConditionValuesProps) => {
  const anchorRef = useRef<HTMLButtonElement>(null);

  // Extract string values for autocomplete (needed for non-KeyValue conditions)
  const stringValues = values
    .map((v) => (typeof v === "string" ? v : v.value || ""))
    .filter((v): v is string => v !== null && v !== undefined);

  return (
    <>
      <Flex
        gap={2}
        alignItems="center"
        flexWrap="wrap"
        flexGrow={1}
        sx={{ position: "relative" }}
        ref={anchorRef}
      >
        {values.map((value, index) => {
          // Handle both string values and KeyValue objects
          const displayValue =
            typeof value === "string" ? value : value.value || "";
          const valueKey =
            typeof value === "string" ? conditionFormDictionaryKey : value.key;
          const key =
            typeof value === "string"
              ? value
              : `${value.key}-${value.value || ""}`;

          return (
            <Flex alignItems="center" gap={2} key={key}>
              <ConditionTagValue
                // Backend does not support user-friendly labels yet, so for demo purposes we will display value as is
                value={
                  isDictionaryKeySelected ? (
                    <Typography>
                      {valueKey}:{" "}
                      <Typography
                        component="span"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {displayValue}
                      </Typography>
                    </Typography>
                  ) : (
                    options.find((o) => o.value === displayValue)?.label ||
                    displayValue
                  )
                }
                onClick={() => onValueRemove(value)}
                isReadonly={isReadonly}
              />
              {index !== values.length - 1 && (
                <Typography fontWeight={700}> OR </Typography>
              )}
            </Flex>
          );
        })}
        {isKeySelected &&
          !isReadonly &&
          (isLoading ? (
            <CircularProgress />
          ) : (
            <WorkspaceConditionValuesAutocomplete
              values={stringValues}
              name={name}
              conditionFormDictionaryKey={conditionFormDictionaryKey}
              options={options}
              anchorRef={anchorRef}
              onDropdownSearchFieldKeyUp={onDropdownSearchFieldKeyUp}
              onDropdownSearchFieldChange={onDropdownSearchFieldChange}
              onItemClick={onItemClick}
              handleKeyValueKeyClick={handleKeyValueKeyClick}
              handleKeyValueValueClick={handleKeyValueValueClick}
              isValuesDropdownOpen={isValuesDropdownOpen}
              isDictionaryKeySelected={isDictionaryKeySelected}
              onValuesDropdownOpen={onValuesDropdownOpen}
              onValuesDropdownClose={onValuesDropdownClose}
            />
          ))}
        {showRemoveConditionButton && (
          <Flex
            sx={{
              flex: "1 0 auto",
              justifyContent: "flex-end",
              visibility: "hidden",
            }}
            className="delete-condition-button"
          >
            <Button
              variant="text"
              sx={{
                color: (theme) => alpha(theme.palette.error.main, 0.5),
              }}
              endIcon={<Close />}
              onClick={onRemove}
            >
              DELETE CONDITION
            </Button>
          </Flex>
        )}
      </Flex>
    </>
  );
};
